"use client";

import type { SliderOptions } from "@components/composers/slider/Slider";
import type React from "react";

import { Slider, SliderContent, SliderItem, useSlider } from "@components/composers/slider/Slider";
import { SliderControls, SliderNext, SliderPrevious } from "@components/composers/slider/SliderControls";
import { useInView } from "@ui/lib/useInView";
import { cn } from "@ui/lib/utils";
import { breakpoints } from "@ui/tokens/grid";
import { cva } from "class-variance-authority";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface SliderGenericProps {
  alignControls?: "center" | "end" | "start";
  centeredAnimation?: boolean;
  className?: string;
  cta?: React.ReactNode;
  // CSS selector for the image element to center controls on
  // When provided with floatingControls=true, the controls will be positioned at the center of the image
  // instead of the center of the entire slider item. Useful when slider items have text content below images.
  floatingControls?: boolean;
  fullCards?: boolean;
  // Example: imageSelector=".slider-image" will center controls on elements with class "slider-image"
  imageSelector?: string;
  // Add a container around the slider on lg+ breakpoints
  inContainer?: boolean;
  items?: React.ReactNode[];
  itemsPerViewportAtLg?: 3 | 4;
  // Add 1 column width on desktop when there are 3 items
  largeCardsOnDesktop?: boolean;
  // Use 11 columns on mobile instead of 10
  largeCardsOnMobile?: boolean;
  // At md breakpoints item takes 9 cols instead of 6
  largeCardsOnTablet?: boolean;
  noBreakout?: boolean;
  onDark?: boolean;
  // Remove 1 column width on mobile and add an extra padding
  smallCardsOnMobile?: boolean;
}

const sliderControlsVariants = cva("flex mt-24", {
  variants: {
    align: {
      center: "justify-center",
      end: "justify-end",
      start: "justify-start",
      between: "justify-between",
    },
    inContainer: {
      true: "",
      false: "",
    },
  },
});

const ControlWrapper = ({
  onDark,
  alignControls,
  inContainer,
}: {
  alignControls: "center" | "end" | "start";
  inContainer: boolean;
  onDark?: boolean;
}) => {
  const { canScrollNext, canScrollPrev } = useSlider();

  if (!canScrollNext && !canScrollPrev) {
    return null;
  }

  return (
    <div
      className={sliderControlsVariants({
        align: alignControls,
        inContainer,
      })}
    >
      <SliderControls inverse={onDark} />
    </div>
  );
};

export const FloatingControls = ({
  imageSelector,
  sliderRef,
}: {
  imageSelector?: string;
  sliderRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const { canScrollNext, canScrollPrev } = useSlider();
  const [imageCenterY, setImageCenterY] = useState<null | number>(null);

  const calculateImageCenter = useCallback(() => {
    if (!imageSelector || !sliderRef.current) return;

    const sliderElement = sliderRef.current;
    const imageElement = sliderElement.querySelector(imageSelector) as HTMLElement;

    if (imageElement) {
      const sliderRect = sliderElement.getBoundingClientRect();
      const imageRect = imageElement.getBoundingClientRect();

      // Calculate the center Y position of the image relative to the slider
      const imageCenter = imageRect.top + imageRect.height / 2 - sliderRect.top;
      setImageCenterY(imageCenter);
    }
  }, [imageSelector, sliderRef]);

  useEffect(() => {
    if (!imageSelector) return;

    // Calculate on mount
    calculateImageCenter();

    // Recalculate on window resize
    const handleResize = () => {
      calculateImageCenter();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [imageSelector, calculateImageCenter]);

  // Recalculate when slider content changes (e.g., when items are loaded)
  useEffect(() => {
    if (!imageSelector || !sliderRef.current) return;

    const observer = new MutationObserver(() => {
      calculateImageCenter();
    });

    observer.observe(sliderRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    return () => observer.disconnect();
  }, [imageSelector, calculateImageCenter, sliderRef]);

  const controlStyle = useMemo(() => {
    if (imageSelector && imageCenterY !== null) {
      return {
        top: `${imageCenterY}px`,
        transform: "translateY(-50%)",
      };
    }
    // Fallback to center of slider when no imageSelector is provided
    return {
      top: "50%",
      transform: "translateY(-50%)",
    };
  }, [imageSelector, imageCenterY]);

  return (
    <>
      {canScrollPrev && (
        <SliderPrevious
          className='absolute left-8 flex h-40 w-40 items-center justify-center rounded-full p-0 lg:left-2'
          style={controlStyle}
          variant='elevated'
        />
      )}
      {canScrollNext && (
        <SliderNext
          className='absolute right-8 flex h-40 w-40 items-center justify-center rounded-full p-0 lg:right-2'
          style={controlStyle}
          variant='elevated'
        />
      )}
    </>
  );
};

const AnimatedSliderItem = ({
  children,
  centeredAnimation,
  className,
  index,
  inView,
}: {
  centeredAnimation?: boolean;
  children: React.ReactNode;
  className?: string;
  inView: boolean;
  index: number;
}) => {
  if (!centeredAnimation) {
    return <SliderItem className={className}>{children}</SliderItem>;
  }

  const isFirstCard = index === 0;
  const isThirdCard = index === 2;
  const isHiddenCard = index > 2; // Cards after the third

  return (
    <SliderItem className={className}>
      <div
        className={cn(
          "transition-all duration-500 ease-out",
          isFirstCard && "lg:origin-right lg:scale-95",
          isThirdCard && "lg:origin-left lg:scale-95",
          isHiddenCard && "lg:origin-left lg:translate-x-[-5%] lg:scale-90 lg:opacity-0",
          inView && "lg:translate-x-0 lg:scale-100 lg:opacity-100",
        )}
      >
        {children}
      </div>
    </SliderItem>
  );
};

const SliderGeneric = ({
  items = [],
  smallCardsOnMobile = false,
  largeCardsOnMobile = false,
  largeCardsOnDesktop = false,
  largeCardsOnTablet = false,
  fullCards = false,
  inContainer = true,
  noBreakout = false,
  cta,
  itemsPerViewportAtLg = 4,
  alignControls = "start",
  className,
  onDark = false,
  floatingControls = false,
  imageSelector,
  centeredAnimation,
}: SliderGenericProps) => {
  const displaySlider = items?.length > 0;
  const intersectionRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const sliderContainerRef = useRef<HTMLDivElement>(null);

  // Use 40% threshold for centered animation, otherwise default to 10%
  const inView = useInView(intersectionRef, {
    once: false,
    threshold: centeredAnimation ? 0.4 : 0.1,
  });

  const options: SliderOptions = {
    align: "start",
    active: !!items?.length,
    breakpoints: {
      [`(min-width: ${breakpoints.md} )`]: {
        active: !!items?.length,
      },
      [`(min-width: ${breakpoints.lg} )`]: {
        active: items?.length > (largeCardsOnDesktop ? itemsPerViewportAtLg - 1 : itemsPerViewportAtLg),
      },
    },
  };

  return (
    displaySlider && (
      <Slider className={cn(className, inView ? "card-reveal" : "")} opts={options} ref={intersectionRef}>
        <div
          className={cn(
            "breakout !relative overflow-hidden py-4",
            !inContainer ? "!vp-grid" : "px-outer-gutter lg:container lg:breakout-reset lg:!px-0",
            noBreakout && "!breakout-reset !px-0",
          )}
          ref={sliderContainerRef}
        >
          <SliderContent className='relative'>
            {items?.map((item, index) => (
              <AnimatedSliderItem
                centeredAnimation={centeredAnimation}
                className={cn(
                  "pl-0",
                  index > 0 ? "ml-inner-gutter" : "",
                  largeCardsOnTablet ? "md:w-9-cols" : "md:w-6-cols",
                  itemsPerViewportAtLg === 3
                    ? largeCardsOnDesktop
                      ? "lg:w-5-cols-vw"
                      : "lg:w-4-cols-vw"
                    : "lg:w-3-cols",
                  largeCardsOnMobile ? "w-11-cols-vw" : smallCardsOnMobile ? "w-9-cols-vw" : "w-10-cols-vw",
                  fullCards && "w-full",
                )}
                inView={!!inView}
                index={index}
                key={index}
              >
                {item}
              </AnimatedSliderItem>
            ))}
          </SliderContent>
          {floatingControls && <FloatingControls imageSelector={imageSelector} sliderRef={sliderContainerRef} />}
        </div>

        <div
          className={cn(
            "container flex flex-wrap items-end",
            cta
              ? "justify-between"
              : alignControls === "end"
                ? "justify-end"
                : alignControls === "center"
                  ? "justify-center"
                  : "justify-start",
          )}
        >
          {cta}
          {items?.length && !floatingControls && (
            <ControlWrapper alignControls={alignControls} inContainer={!cta && inContainer} onDark={onDark} />
          )}
        </div>
      </Slider>
    )
  );
};

SliderGeneric.displayName = "SliderGeneric";

export { SliderGeneric };
